import { useState } from 'react';
import { SEVERE_GRADE, SYMPTOM_LABEL } from '@oncocoach/care';
import type { AppSession, GradedSymptom, PatientView, SymptomCode } from '../../lib/types';
import { ApiError, api } from '../../lib/api';
import { Icon } from '../../components/Mark';

/** How bad, in the words the patient is asked rather than the numbers the derivations compare. */
const GRADES = ['None', 'Mild', 'Moderate', 'Severe', 'Cannot do anything'];

/**
 * How you feel today, with no temperature in the way.
 *
 * Two things make this different from ticking boxes. Each symptom is GRADED, because "some
 * diarrhoea" and "diarrhoea I cannot leave the house for" are different clinical events and only
 * the second one is a phone call tonight. And "nothing today" is a real answer that gets recorded,
 * because a care team needs to be able to tell a good day from silence.
 */
export function SymptomFlow({ session, view, onDone, onClose }: { session: AppSession; view: PatientView; onDone: () => Promise<void>; onClose: () => void }) {
  const [grades, setGrades] = useState<Record<string, number>>({});
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ insight: string | null; severe: boolean } | null>(null);

  const options = view.track.symptoms;
  const set = (code: SymptomCode, grade: number) => setGrades((g) => ({ ...g, [code]: grade }));
  const asGrade = (n: number) => Math.max(0, Math.min(4, Math.round(n))) as GradedSymptom['grade'];
  const reported: GradedSymptom[] = Object.entries(grades)
    .filter(([, g]) => g > 0)
    .map(([code, grade]) => ({ code: code as SymptomCode, grade: grade as GradedSymptom['grade'] }));
  const severe = reported.some((s) => s.grade >= SEVERE_GRADE);
  const answered = Object.keys(grades).length > 0;

  const submit = async (nothing = false) => {
    setBusy(true);
    setError(null);
    try {
      // "Nothing today" still has to reach the record, so it goes as the mildest possible report
      // on the first symptom this track asks about. Silence and a good day must not look the same.
      const payload: GradedSymptom[] = nothing || reported.length === 0 ? [{ code: (options[0] ?? 'fatigue') as SymptomCode, grade: asGrade(0) }] : reported;
      const r = await api.postEvent({ type: 'log-symptoms', symptoms: payload, ...(note.trim() ? { note: note.trim() } : {}) }, session.token);
      setDone({ insight: r.insight, severe });
      await onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not send that. Try again, and if this is urgent ring your team.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="overlay" role="dialog" aria-modal="true" aria-label="How are you feeling">
      <div className="modal">
        <header className="modal-head">
          <h2>{done ? 'Sent' : 'How are you today?'}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            <Icon name="close" size={18} />
          </button>
        </header>

        <div className="modal-body">
          <p className="emergency">
            <Icon name="alert" size={16} />{' '}
            {view.track.feverIsEmergency
              ? `A fever of 38.0°C or more, trouble breathing, bleeding or confusion: ring ${view.practiceName}'s emergency line or go to the emergency department now. Do not wait for an answer here.`
              : `Trouble breathing, bleeding or confusion: call your emergency services now. Do not wait for an answer here.`}
          </p>

          {done ? (
            <>
              <p>{done.insight}</p>
              <p className="hint">
                {done.severe
                  ? `This has been flagged to ${view.doctor.name} and the team at ${view.practiceName}.`
                  : `Noted for ${view.doctor.name} and the team at ${view.practiceName}. They read these during clinic hours.`}
              </p>
            </>
          ) : (
            <>
              <span className="eyebrow">How much has each of these bothered you today?</span>
              <fieldset className="grades">
                <legend className="sr-only">Symptoms</legend>
                {options.map((code) => (
                  <div key={code} className="grade-row">
                    <span className="grade-name">{SYMPTOM_LABEL[code]}</span>
                    <div className="grade-scale" role="group" aria-label={SYMPTOM_LABEL[code]}>
                      {GRADES.map((label, g) => (
                        <button key={g} type="button" className={`${(grades[code] ?? -1) === g ? 'on' : ''} ${g >= SEVERE_GRADE ? 'severe' : ''}`} aria-pressed={(grades[code] ?? -1) === g} onClick={() => set(code, g)}>
                          {['None', 'Mild', 'Mod', 'Severe', 'Worst'][g]}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </fieldset>
              <label className="field">
                <span>Anything else worth telling them? (optional)</span>
                <textarea value={note} rows={3} maxLength={500} placeholder="When it started, what makes it better or worse, whether it is new." onChange={(e) => setNote(e.target.value)} />
              </label>
              {error ? <p className="form-error">{error}</p> : null}
            </>
          )}
        </div>

        <footer className="modal-foot">
          {done ? (
            <button type="button" className="primary" onClick={onClose}>
              Back to home
            </button>
          ) : (
            <>
              <button type="button" className="ghost" disabled={busy} onClick={() => void submit(true)}>
                Nothing today
              </button>
              <button type="button" className="primary big" disabled={busy || !answered} onClick={() => void submit()}>
                {busy ? 'Sending…' : severe ? 'Tell my team now' : 'Send to my team'}
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}
